import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NxtRemoteControlScreenService } from '../services/nxt-remote-control-screen.service';
import { RemoteControlEventArgs, RemoteControlService } from '../services/remote-control.service';

declare let ResizeObserver;

@Component({
  selector: 'xs-remote-control-screen-panel',
  templateUrl: './remote-control-screen-panel.component.html',
  styleUrls: ['./remote-control-screen-panel.component.scss'],
})
export class RemoteControlScreenPanelComponent implements OnDestroy {
  @Input() supportsInput: boolean;
  @Output() onCanvasReady = new EventEmitter();
  @Input() popOut = false;

  private rcScreenContainer: HTMLDivElement;
  private rcVideo: HTMLVideoElement;
  private rcCanvas: HTMLCanvasElement;
  private rcInputCanvas: HTMLCanvasElement;

  resizeObserver: typeof ResizeObserver;
  private subscriptions = new Subscription();
  constructor(
    private remoteControlScreenService: NxtRemoteControlScreenService,
    private remoteControlService: RemoteControlService,
    private cd: ChangeDetectorRef,
  ) {

    /* this subscription will fire when a video stream starts for the first time */
    this.subscriptions.add(
      this.remoteControlService.RemoteControlStarted.pipe(filter(result => result.isStreamingRC))
      .subscribe( (args: RemoteControlEventArgs) => {
        this.startVideoStream(args.deviceId);
      })
    );

    /* this subscription will fire when a video stream is embedded (do not call setup) */
    this.subscriptions.add(
      this.remoteControlService.RemoteControlEmbedFinished.pipe(filter(result => result.isStreamingRC)).subscribe(_ => {
        this.configureElementRefs();
        this.configureRcScreenSizeChangeDetection();
        this.updatercScreenContainerHeight();
      })
    );
  }

  configureElementRefs = ()=> {
    this.rcScreenContainer = document.getElementById('rcScreenContainer') as HTMLDivElement;
    this.rcVideo = document.getElementById('rcVideo') as HTMLVideoElement;
    this.rcCanvas = document.getElementById('rcCanvas') as HTMLCanvasElement;
    this.rcInputCanvas = document.getElementById('rcInputCanvas') as HTMLCanvasElement;
  };

  startVideoStream(deviceId: string) {
    this.configureElementRefs();

    this.remoteControlScreenService.setup(
      deviceId,
      this.rcCanvas,
      this.rcInputCanvas,
      this.rcVideo,
      this.popOut,
      true
    );

    // RemoteControlService.startRemoteControl() and transferDeviceState() are waiting for this event
    this.configureRcScreenSizeChangeDetection();
    this.updatercScreenContainerHeight();
    this.remoteControlService.setRcCanvasReady();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      delete this.resizeObserver;
    }
    this.subscriptions.unsubscribe();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.updatercScreenContainerHeight();
  }

  private configureRcScreenSizeChangeDetection = () => {
    this.resizeObserver = new ResizeObserver(this.updatercScreenContainerHeight);
    this.resizeObserver.observe(this.rcScreenContainer);
  };

  /** Updates the containers height to occupy all the available space */
  private updatercScreenContainerHeight = (): void => {
    // Canvas should always be fully stretched within the available space in the rcScreenContainer
    if (this.rcCanvas !== undefined) {
      this.rcCanvas.width = this.rcScreenContainer.clientWidth;
      this.rcCanvas.height = this.rcScreenContainer.clientHeight;
      this.rcInputCanvas.width = this.rcScreenContainer.clientWidth;
      this.rcInputCanvas.height = this.rcScreenContainer.clientHeight;
      this.remoteControlScreenService.updateRcScreenConfiguration();
    }
};
}
